import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import * as signalR from "@microsoft/signalr";
import { Box, Fade, IconButton, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import FeatherIcon from "feather-icons-react";
import confetti from "canvas-confetti";
import { startDiwaliShow } from "./diwaliGrandShow";
import { GetUserDetail } from "../services/user";

const HUB_ROUTE = "/dashboardHub";

const HUB_EVENTS = Object.freeze({
    ONLINE_USERS_CHANGED: "DashboardOnlineUsersChanged",
    SHOW_CELEBRATION: "ShowDashboardCelebration",
    CELEBRATION_STATE_CHANGED: "DashboardCelebrationStateChanged",
    CELEBRATED_USERS_CHANGED: "DashboardCelebratedUsersChanged",
});

const HUB_METHODS = Object.freeze({
    REGISTER_USER: "RegisterDashboardUser",
    UPDATE_PRESENCE: "UpdateDashboardPresence",
    GET_ONLINE_USERS: "GetOnlineDashboardUsers",
    GET_MY_COMPLETED_CELEBRATION_KEYS: "GetMyCompletedCelebrationKeys",
    GET_MY_RECEIVED_CELEBRATIONS: "GetMyReceivedCelebrationSummaries",
    GET_MY_PENDING_CELEBRATIONS: "GetMyPendingCelebrations",
    ACKNOWLEDGE_CELEBRATION: "AcknowledgeCelebrationDisplayed",
    SEND_TEST_CELEBRATION: "SendTestCelebration",
    BROADCAST_CELEBRATION: "BroadcastCelebration",
});

const CONNECTION_STATUS = Object.freeze({
    CONNECTING: "Connecting",
    CONNECTED: "Connected",
    RECONNECTING: "Reconnecting",
    DISCONNECTED: "Disconnected",
    FAILED: "Failed",
    CONFIGURATION_MISSING: "Configuration Missing",
});

const AUTOMATIC_RECONNECT_DELAYS = [0, 2000, 5000, 10000, 30000];

const INITIAL_START_RETRY_DELAYS = [0, 2000, 5000, 10000];

const MANUAL_RESTART_DELAYS = [0, 5000, 10000, 30000];

const wait = (milliseconds) =>
    new Promise((resolve) => {
        window.setTimeout(resolve, milliseconds);
    });

const normalizeDashboardHubBaseUrl = (value) =>
    String(value ?? "")
        .trim()
        .replace(/\/v1\.0\/?$/i, "")
        .replace(/\/+$/, "");

const resolveDashboardHubBaseUrl = () => {
    if (typeof window !== "undefined") {
        const protocol = window.location.protocol;
        const hostname = window.location.hostname.toLowerCase();
        const port = window.location.port;

        // LIVE
        if (hostname === "dashboard.sswhite.co") {
            return "https://dashboardapi.sswhite.co";
        }

        // LOCAL IIS
        // FE: https://192.168.1.117:85
        // BE: https://192.168.1.117:81
        if (
            hostname === "192.168.1.117" &&
            port === "85"
        ) {
            return "https://192.168.1.117:81";
        }

        // LOCAL IIS using localhost
        if (
            (hostname === "localhost" ||
                hostname === "127.0.0.1") &&
            port === "85"
        ) {
            return "https://192.168.1.117:81";
        }

        // React development server
        if (
            (hostname === "localhost" ||
                hostname === "127.0.0.1") &&
            port === "3000"
        ) {
            return "http://localhost:18344";
        }
    }

    const configuredUrl =
        process.env.REACT_APP_SIGNALR_API_URL ||
        process.env.REACT_APP_URL ||
        "";

    return String(configuredUrl)
        .trim()
        .replace(/\/v1\.0\/?$/i, "")
        .replace(/\/+$/, "");
};

const normalizeOnlineUsers = (users) => {
    if (!Array.isArray(users)) {
        return [];
    }

    const uniqueUsers = new Map();

    users.forEach((user) => {
        const personId = user?.personId ?? user?.PersonId;

        if (
            personId === undefined ||
            personId === null ||
            String(personId).trim() === ""
        ) {
            return;
        }

        const normalizedPersonId = String(personId).trim();

        const personName = String(
            user?.personName ?? user?.PersonName ?? `User ${normalizedPersonId}`,
        ).trim();

        const connectionCount = Math.max(
            1,
            Number(user?.connectionCount ?? user?.ConnectionCount ?? 1) || 1,
        );

        const isVisible = Boolean(user?.isVisible ?? user?.IsVisible ?? false);

        const lastActivityAt = user?.lastActivityAt ?? user?.LastActivityAt ?? null;

        uniqueUsers.set(normalizedPersonId, {
            ...user,
            personId: normalizedPersonId,
            personName: personName || `User ${normalizedPersonId}`,
            connectionCount,
            isVisible,
            lastActivityAt,
        });
    });

    return Array.from(uniqueUsers.values()).sort((firstUser, secondUser) =>
        firstUser.personName.localeCompare(secondUser.personName, undefined, {
            sensitivity: "base",
        }),
    );
};

const isConnected = (connection) =>
    connection?.state === signalR.HubConnectionState.Connected;

const normalizeCelebrationKeys = (keys) =>
    Array.from(
        new Set(
            (Array.isArray(keys) ? keys : [])
                .map((key) => String(key ?? "").trim())
                .filter(Boolean),
        ),
    );

const normalizeCelebratedUsers = (users) => {
    const uniqueUsers = new Map();

    (Array.isArray(users) ? users : []).forEach((user) => {
        const personId = String(
            user?.personId ??
            user?.PersonId ??
            user?.senderPersonId ??
            user?.SenderPersonId ??
            "",
        ).trim();

        const personName = String(
            user?.personName ??
            user?.PersonName ??
            user?.senderPersonName ??
            user?.SenderPersonName ??
            user ??
            "",
        )
            .trim()
            .replace(/\s+/g, " ");

        if (!personName) {
            return;
        }

        const uniqueKey = personId || personName.toLowerCase();

        if (!uniqueUsers.has(uniqueKey)) {
            uniqueUsers.set(uniqueKey, {
                personId,
                personName,
            });
        }
    });

    return Array.from(uniqueUsers.values());
};

const mergeCelebratedUsers = (...userGroups) =>
    normalizeCelebratedUsers(userGroups.flat());

const isApplicationActive = () => {
    if (typeof document === "undefined") {
        return true;
    }

    const isVisible = document.visibilityState === "visible";

    const hasFocus =
        typeof document.hasFocus === "function" ? document.hasFocus() : true;

    return isVisible && hasFocus;
};

const getCelebrationIdentity = (celebration) => {
    const celebrationRecordId = String(
        celebration?.celebrationRecordId ??
        celebration?.CelebrationRecordId ??
        "",
    ).trim();

    if (celebrationRecordId) {
        return `database-${celebrationRecordId}`;
    }

    return String(
        celebration?.celebrationId ||
        celebration?.CelebrationId ||
        [
            celebration?.celebrationKey ??
            celebration?.CelebrationKey,
            celebration?.sentBy ??
            celebration?.SentBy,
            celebration?.sentAt ??
            celebration?.SentAt,
            celebration?.title ??
            celebration?.Title,
        ].join("-"),
    );
};

const SPARKLES = [
    {
        left: "7%",
        top: "15%",
        size: 26,
        duration: 2.9,
        delay: 0,
        colorIndex: 1,
    },
    {
        left: "15%",
        top: "42%",
        size: 15,
        duration: 3.7,
        delay: 0.4,
        colorIndex: 3,
    },
    {
        left: "9%",
        top: "76%",
        size: 21,
        duration: 3.2,
        delay: 0.8,
        colorIndex: 5,
    },
    {
        left: "28%",
        top: "12%",
        size: 17,
        duration: 2.8,
        delay: 1.2,
        colorIndex: 2,
    },
    {
        right: "7%",
        top: "15%",
        size: 25,
        duration: 3.4,
        delay: 0.3,
        colorIndex: 4,
    },
    {
        right: "16%",
        top: "44%",
        size: 16,
        duration: 2.7,
        delay: 0.9,
        colorIndex: 0,
    },
    {
        right: "9%",
        top: "76%",
        size: 22,
        duration: 3.8,
        delay: 0.6,
        colorIndex: 2,
    },
    {
        right: "29%",
        top: "12%",
        size: 16,
        duration: 3,
        delay: 1.3,
        colorIndex: 1,
    },
];

const BALLOONS = [
    {
        left: "5%",
        top: "23%",
        size: 58,
        duration: 6,
        delay: 0,
        colorIndex: 0,
    },
    {
        left: "12%",
        top: "61%",
        size: 40,
        duration: 7,
        delay: 0.8,
        colorIndex: 1,
    },
    {
        left: "21%",
        top: "12%",
        size: 34,
        duration: 6.5,
        delay: 1.1,
        colorIndex: 2,
    },
    {
        right: "5%",
        top: "24%",
        size: 55,
        duration: 6.8,
        delay: 0.4,
        colorIndex: 3,
    },
    {
        right: "13%",
        top: "62%",
        size: 41,
        duration: 5.9,
        delay: 1.2,
        colorIndex: 4,
    },
    {
        right: "22%",
        top: "11%",
        size: 33,
        duration: 7.4,
        delay: 0.7,
        colorIndex: 5,
    },
];

const CelebrationOverlay = ({ celebration, onClose }) => {
    const theme = useTheme();

    const canvasRef = useRef(null);
    const onCloseRef = useRef(onClose);
    const isClosingRef = useRef(false);

    const celebrationKey =
        celebration?.celebrationId ||
        [
            celebration?.celebrationType,
            celebration?.personId,
            celebration?.targetPersonId,
            celebration?.sentAt,
            celebration?.title,
        ].join("-");

    const isDiwali =
        String(celebration?.celebrationType || "").toLowerCase() === "diwali";

    const reducedMotion =
        typeof window !== "undefined" &&
        Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    const handleClose = useCallback(() => {
        if (isClosingRef.current) {
            return;
        }

        isClosingRef.current = true;

        if (typeof onCloseRef.current === "function") {
            onCloseRef.current();
        }
    }, []);

    useEffect(() => {
        if (!celebration) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                handleClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [celebrationKey, celebration, handleClose]);

    useEffect(() => {
        if (!celebration) {
            return undefined;
        }

        const originalBodyOverflow = document.body.style.overflow;

        const originalHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = "hidden";

        document.documentElement.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = originalBodyOverflow;

            document.documentElement.style.overflow = originalHtmlOverflow;
        };
    }, [celebrationKey, celebration]);

    const fireworkColors = useMemo(
        () =>
            isDiwali
                ? [
                    "#fff1b8",
                    "#ffd166",
                    "#ff9f1c",
                    "#ff5d3d",
                    "#ff3f81",
                    "#b967ff",
                    "#35d7ff",
                    "#fffaf0",
                    "#ffbe0b",
                ]
                : [
                    theme.palette.primary.main,
                    theme.palette.secondary.main,
                    theme.palette.warning.main,
                    theme.palette.success.main,
                    theme.palette.info.main,
                    theme.palette.error.main,
                    theme.palette.common.white,
                ],
        [
            isDiwali,
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.warning.main,
            theme.palette.success.main,
            theme.palette.info.main,
            theme.palette.error.main,
            theme.palette.common.white,
        ],
    );

    useEffect(() => {
        if (!celebration) {
            return undefined;
        }

        isClosingRef.current = false;

        const canvas = canvasRef.current;

        if (!canvas) {
            return undefined;
        }

        // Diwali uses a purpose-built canvas renderer instead of confetti particles.
        // This produces recognisable upward rockets, glowing trails and round aerial bursts.
     if (isDiwali) {
    const diwaliDuration = reducedMotion
        ? 3500
        : Number(celebration.autoCloseMilliseconds) || 24000;

    return startDiwaliShow(canvas, {
        duration: diwaliDuration,
        reducedMotion,
        onFinish: handleClose,
    });
}

        const fireworks = confetti.create(canvas, {
            resize: true,
            useWorker: true,
        });

        const duration = reducedMotion
            ? 3500
            : Number(celebration.autoCloseMilliseconds) || (isDiwali ? 16000 : 10000);

        const endingTime = Date.now() + duration;

        const timers = [];
        let fireworkInterval = null;

        const getBurstColors = () => {
            const firstIndex = Math.floor(Math.random() * fireworkColors.length);

            const secondIndex = (firstIndex + 2) % fireworkColors.length;

            const thirdIndex = (firstIndex + 4) % fireworkColors.length;

            return [
                fireworkColors[firstIndex],
                fireworkColors[secondIndex],
                fireworkColors[thirdIndex],
                theme.palette.common.white,
            ];
        };

        const launchCircularFirework = ({
            x,
            y,
            particleCount = 110,
            velocity = 48,
            scalar = 1.2,
        }) => {
            fireworks({
                particleCount: reducedMotion
                    ? Math.min(particleCount, 28)
                    : particleCount,

                startVelocity: velocity,
                spread: 360,
                ticks: 175,
                gravity: 0.52,
                decay: 0.9,
                scalar,
                drift: 0,

                shapes: ["circle", "star"],

                origin: {
                    x,
                    y,
                },

                colors: getBurstColors(),
            });
        };

        const launchLargeFirework = (x, y) => {
            launchCircularFirework({
                x,
                y,
                particleCount: isDiwali ? 190 : 135,
                velocity: isDiwali ? 58 : 54,
                scalar: isDiwali ? 1.5 : 1.35,
            });

            if (!reducedMotion) {
                timers.push(
                    window.setTimeout(() => {
                        launchCircularFirework({
                            x,
                            y,
                            particleCount: isDiwali ? 95 : 65,
                            velocity: isDiwali ? 37 : 32,
                            scalar: isDiwali ? 1 : 0.85,
                        });
                    }, isDiwali ? 115 : 130),
                );
            }
        };

        const launchSideCannons = () => {
            if (reducedMotion) {
                return;
            }

            fireworks({
                particleCount: isDiwali ? 110 : 75,
                angle: 58,
                spread: isDiwali ? 44 : 38,
                startVelocity: isDiwali ? 66 : 62,
                ticks: 190,
                gravity: 0.75,
                decay: 0.92,
                scalar: isDiwali ? 1.22 : 1.15,

                shapes: ["circle", "star"],

                origin: {
                    x: 0,
                    y: 0.95,
                },

                colors: fireworkColors,
            });

            fireworks({
                particleCount: isDiwali ? 110 : 75,
                angle: 122,
                spread: isDiwali ? 44 : 38,
                startVelocity: isDiwali ? 66 : 62,
                ticks: 190,
                gravity: 0.75,
                decay: 0.92,
                scalar: isDiwali ? 1.22 : 1.15,

                shapes: ["circle", "star"],

                origin: {
                    x: 1,
                    y: 0.95,
                },

                colors: fireworkColors,
            });
        };

        const launchDiwaliRocket = (x, targetY) => {
            fireworks({
                particleCount: reducedMotion ? 8 : 18,
                angle: 90,
                spread: 7,
                startVelocity: 62,
                ticks: 72,
                gravity: 0.82,
                decay: 0.94,
                scalar: 0.9,
                drift: (Math.random() - 0.5) * 0.35,
                shapes: ["circle"],
                origin: { x, y: 0.98 },
                colors: ["#fff1b8", "#ffd166", "#ff9f1c", "#ffffff"],
            });

            if (!reducedMotion) {
                timers.push(
                    window.setTimeout(() => launchLargeFirework(x, targetY), 420),
                );
            }
        };

        const launchOpeningSequence = () => {
            launchLargeFirework(0.22, 0.24);

            launchLargeFirework(0.78, 0.22);

            launchSideCannons();
        };

        if (isDiwali) {
            // Grand festival opening: triple aerial bursts, side cannons and rising rockets.
            timers.push(
                window.setTimeout(() => {
                    launchLargeFirework(0.16, 0.24);
                    launchLargeFirework(0.5, 0.15);
                    launchLargeFirework(0.84, 0.23);
                    launchSideCannons();
                }, 140),
            );
            timers.push(
                window.setTimeout(() => {
                    launchDiwaliRocket(0.09, 0.31);
                    launchDiwaliRocket(0.29, 0.23);
                    launchDiwaliRocket(0.5, 0.18);
                    launchDiwaliRocket(0.71, 0.24);
                    launchDiwaliRocket(0.91, 0.3);
                }, 520),
            );
            timers.push(
                window.setTimeout(() => {
                    launchLargeFirework(0.29, 0.36);
                    launchLargeFirework(0.71, 0.34);
                    launchCircularFirework({
                        x: 0.5,
                        y: 0.32,
                        particleCount: 150,
                        velocity: 49,
                        scalar: 1.35,
                    });
                }, 1280),
            );
        } else {
            timers.push(window.setTimeout(launchOpeningSequence, 180));

            timers.push(
                window.setTimeout(() => {
                    launchLargeFirework(0.5, 0.16);
                }, 650),
            );

            timers.push(
                window.setTimeout(() => {
                    launchCircularFirework({
                        x: 0.12,
                        y: 0.42,
                        particleCount: 95,
                        velocity: 45,
                        scalar: 1.1,
                    });
                }, 1050),
            );

            timers.push(
                window.setTimeout(() => {
                    launchCircularFirework({
                        x: 0.88,
                        y: 0.4,
                        particleCount: 95,
                        velocity: 45,
                        scalar: 1.1,
                    });
                }, 1250),
            );
        }

        if (!reducedMotion) {
            fireworkInterval = window.setInterval(() => {
                if (Date.now() >= endingTime - (isDiwali ? 1700 : 1400)) {
                    if (fireworkInterval) {
                        window.clearInterval(fireworkInterval);
                    }

                    return;
                }

                const x = 0.08 + Math.random() * 0.84;
                const y = 0.08 + Math.random() * (isDiwali ? 0.4 : 0.34);

                if (isDiwali) {
                    const roll = Math.random();

                    if (roll < 0.16) {
                        launchSideCannons();
                    } else if (roll < 0.43) {
                        launchDiwaliRocket(x, y);
                    } else if (roll < 0.72) {
                        launchLargeFirework(x, y);
                    } else {
                        launchCircularFirework({
                            x,
                            y,
                            particleCount: 120 + Math.floor(Math.random() * 60),
                            velocity: 45 + Math.random() * 15,
                            scalar: 1.12 + Math.random() * 0.38,
                        });
                    }

                    if (Math.random() < 0.24) {
                        launchCircularFirework({
                            x: Math.max(0.06, Math.min(0.94, 1 - x)),
                            y: 0.13 + Math.random() * 0.3,
                            particleCount: 80 + Math.floor(Math.random() * 55),
                            velocity: 43 + Math.random() * 10,
                            scalar: 1.05 + Math.random() * 0.3,
                        });
                    }

                    return;
                }

                if (Math.random() < 0.2) {
                    launchSideCannons();
                    return;
                }

                if (Math.random() < 0.4) {
                    launchLargeFirework(x, y);
                    return;
                }

                launchCircularFirework({
                    x,
                    y,
                    particleCount: 85 + Math.floor(Math.random() * 40),
                    velocity: 42 + Math.random() * 12,
                    scalar: 1 + Math.random() * 0.35,
                });
            }, isDiwali ? 720 : 950);
        }

        timers.push(
            window.setTimeout(() => {
                launchSideCannons();

                if (isDiwali) {
                    launchLargeFirework(0.1, 0.2);
                    launchLargeFirework(0.3, 0.14);
                    launchLargeFirework(0.5, 0.1);
                    launchLargeFirework(0.7, 0.14);
                    launchLargeFirework(0.9, 0.2);
                    fireworks({
                        particleCount: reducedMotion ? 24 : 170,
                        spread: 150,
                        startVelocity: 34,
                        ticks: 230,
                        gravity: 1.1,
                        decay: 0.92,
                        scalar: 1.2,
                        shapes: ["circle", "star"],
                        origin: { x: 0.5, y: 0.56 },
                        colors: ["#fff1b8", "#ffd166", "#ff9f1c", "#ff5d3d", "#ffffff"],
                    });
                } else {
                    launchLargeFirework(0.2, 0.2);
                    launchLargeFirework(0.5, 0.14);
                    launchLargeFirework(0.8, 0.2);
                }
            }, Math.max(0, duration - 1000)),
        );

        timers.push(window.setTimeout(handleClose, duration));

        return () => {
            timers.forEach((timer) => {
                window.clearTimeout(timer);
            });

            if (fireworkInterval) {
                window.clearInterval(fireworkInterval);
            }

            try {
                fireworks.reset();
            } catch (error) {
                console.error("Unable to reset celebration fireworks:", error);
            }
        };
    }, [
        celebrationKey,
        celebration,
        fireworkColors,
        handleClose,
        reducedMotion,
        theme.palette.common.white,
        isDiwali,
    ]);

    if (!celebration) {
        return null;
    }

    const celebrationType = String(
        celebration.celebrationType || "",
    ).toLowerCase();

    const isBirthday = celebrationType === "birthday";

    const isAnniversary = celebrationType === "anniversary";

    const personName =
        celebration.personName ||
        celebration.targetPersonName ||
        "Valued Team Member";

    const years = Number(celebration.years) || 1;

    const celebrationLabel = isDiwali
        ? "Festival of Lights · Grand Celebration"
        : isBirthday
            ? "Birthday Celebration"
            : isAnniversary
                ? "Work Anniversary Celebration"
                : "Live Celebration";

    const labelIcon = isDiwali ? "sun" : isBirthday ? "gift" : isAnniversary ? "award" : "zap";

    const heroSymbol = isDiwali ? "🪔" : isAnniversary ? "🏆" : "🎂";

    const heroLabel = isDiwali
        ? "Festival of lights diya"
        : isAnniversary
            ? "Anniversary trophy"
            : "Birthday cake";

    const mainHeading = isDiwali
        ? "Happy Diwali"
        : isBirthday
            ? "Happy Birthday"
            : isAnniversary
                ? `Happy ${years}-Year Work Anniversary`
                : celebration.title || "Congratulations";

    const showSeparateName = isBirthday || isAnniversary;

    const computedMessage =
        celebration.message ||
        (isDiwali
            ? "May the lights of Diwali fill every heart with joy, every home with prosperity, and every new beginning with possibility. Wishing our entire team a bright and beautiful festival!"
            : isBirthday
                ? "Wishing you a wonderful day filled with happiness, success and unforgettable moments!"
                : isAnniversary
                    ? "Thank you for your dedication, passion and valuable contribution to our journey!"
                    : "Thank you for being an extraordinary part of our success!");

    const durationMs = reducedMotion
        ? 3500
        : Number(celebration.autoCloseMilliseconds) || (isDiwali ? 16000 : 10000);

    const rainbowGradient = isDiwali
        ? "linear-gradient(90deg, #fff1b8 0%, #ffd166 18%, #ff9f1c 36%, #ff5d3d 52%, #ff3f81 68%, #b967ff 84%, #35d7ff 100%)"
        : `
        linear-gradient(
          90deg,
          ${theme.palette.warning.main},
          ${theme.palette.secondary.main},
          ${theme.palette.error.main},
          ${theme.palette.info.main},
          ${theme.palette.success.main},
          ${theme.palette.primary.main},
          ${theme.palette.warning.main}
        )
      `;

    return (
        <Fade in timeout={450}>
            <Box
                role="dialog"
                aria-modal="true"
                aria-label={celebration.title || `${mainHeading} ${personName}`}
                sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: (currentTheme) => currentTheme.zIndex.modal + 3000,

                    overflowX: "hidden",
                    overflowY: "auto",

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    px: {
                        xs: 2,
                        sm: 3,
                    },

                    py: {
                        xs: 5,
                        md: 4,
                    },

                    color: "common.white",

                    background: (currentTheme) => `
            radial-gradient(
              circle at 50% 44%,
              ${alpha(
                        isDiwali
                            ? "#ffae2f"
                            : isBirthday
                                ? currentTheme.palette.secondary.main
                                : isAnniversary
                                    ? currentTheme.palette.warning.main
                                    : currentTheme.palette.info.main,
                        isDiwali ? 0.32 : 0.25,
                    )} 0%,
              transparent 31%
            ),
            radial-gradient(
              circle at 12% 20%,
              ${alpha(currentTheme.palette.primary.main, 0.3)} 0%,
              transparent 40%
            ),
            radial-gradient(
              circle at 88% 18%,
              ${alpha(currentTheme.palette.info.main, 0.27)} 0%,
              transparent 40%
            ),
            radial-gradient(
              circle at 16% 88%,
              ${alpha(currentTheme.palette.success.main, 0.2)} 0%,
              transparent 42%
            ),
            radial-gradient(
              circle at 84% 88%,
              ${alpha(currentTheme.palette.warning.main, 0.2)} 0%,
              transparent 42%
            ),
            ${alpha(currentTheme.palette.common.black, 0.91)}
          `,

                    "@keyframes celebrationRise": {
                        "0%": {
                            opacity: 0,
                            transform: "translateY(35px) scale(0.88)",
                        },
                        "100%": {
                            opacity: 1,
                            transform: "translateY(0) scale(1)",
                        },
                    },

                    "@keyframes celebrationRainbow": {
                        "0%": {
                            backgroundPosition: "0% 50%",
                        },
                        "50%": {
                            backgroundPosition: "100% 50%",
                        },
                        "100%": {
                            backgroundPosition: "0% 50%",
                        },
                    },

                    "@keyframes celebrationFloat": {
                        "0%, 100%": {
                            transform: "translateY(0) rotate(-2deg)",
                        },
                        "50%": {
                            transform: "translateY(-15px) rotate(2deg)",
                        },
                    },

                    "@keyframes celebrationCakeEntrance": {
                        "0%": {
                            opacity: 0,
                            transform: "translateY(30px) scale(0.4) rotate(-12deg)",
                        },
                        "70%": {
                            opacity: 1,
                            transform: "translateY(-8px) scale(1.12) rotate(3deg)",
                        },
                        "100%": {
                            opacity: 1,
                            transform: "translateY(0) scale(1) rotate(0deg)",
                        },
                    },

                    "@keyframes celebrationGlow": {
                        "0%, 100%": {
                            opacity: 0.45,
                            transform: "scale(0.92)",
                        },
                        "50%": {
                            opacity: 0.85,
                            transform: "scale(1.15)",
                        },
                    },

                    "@keyframes celebrationTwinkle": {
                        "0%, 100%": {
                            opacity: 0.2,
                            transform: "scale(0.7) rotate(0deg)",
                        },
                        "50%": {
                            opacity: 1,
                            transform: "scale(1.3) rotate(25deg)",
                        },
                    },

                    "@keyframes celebrationBalloon": {
                        "0%": {
                            transform: "translateY(10px) rotate(-3deg)",
                        },
                        "100%": {
                            transform: "translateY(-22px) rotate(4deg)",
                        },
                    },

                    "@keyframes celebrationProgress": {
                        "0%": {
                            transform: "scaleX(1)",
                        },
                        "100%": {
                            transform: "scaleX(0)",
                        },
                    },

                    "@keyframes diwaliSkyReveal": {
                        "0%": { opacity: 0, transform: "scale(1.16)" },
                        "12%": { opacity: 0.82 },
                        "42%": { opacity: 0.42 },
                        "100%": { opacity: 0.04, transform: "scale(1)" },
                    },

                    "@keyframes diwaliShockwave": {
                        "0%": { opacity: 0, transform: "translate(-50%, -50%) scale(0.12)" },
                        "12%": { opacity: 0.9 },
                        "72%": { opacity: 0.38 },
                        "100%": { opacity: 0, transform: "translate(-50%, -50%) scale(3.1)" },
                    },

                    "@keyframes diwaliHeroImpact": {
                        "0%": { opacity: 0, transform: "translateY(58px) scale(0.42) rotate(-11deg)" },
                        "58%": { opacity: 1, transform: "translateY(-11px) scale(1.13) rotate(2deg)" },
                        "78%": { transform: "translateY(3px) scale(0.97) rotate(-1deg)" },
                        "100%": { opacity: 1, transform: "translateY(0) scale(1) rotate(0deg)" },
                    },

                    "@keyframes diwaliTitleSlam": {
                        "0%": { opacity: 0, transform: "translateY(74px) scale(0.58)" },
                        "58%": { opacity: 1, transform: "translateY(-10px) scale(1.10)" },
                        "77%": { transform: "translateY(3px) scale(0.98)" },
                        "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
                    },

                    "@keyframes diwaliLightSweep": {
                        "0%": { opacity: 0, transform: "translateX(-35%) skewX(-14deg)" },
                        "24%": { opacity: 0.72 },
                        "100%": { opacity: 0, transform: "translateX(48%) skewX(-14deg)" },
                    },
                }}
            >
                <canvas
                    ref={canvasRef}
                    aria-hidden="true"
                    style={{
                        position: "fixed",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                        zIndex: 3,
                    }}
                />

                <Box
                    aria-hidden="true"
                    sx={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 1,
                        pointerEvents: "none",
                        overflow: "hidden",
                    }}
                >
                    {isDiwali && !reducedMotion && (
                        <>
                            <Box
                                aria-hidden="true"
                                sx={{
                                    position: "absolute",
                                    inset: "-8%",
                                    opacity: 0,
                                    transformOrigin: "50% 44%",
                                    mixBlendMode: "screen",
                                    background: "radial-gradient(circle at 50% 44%, rgba(255,247,210,0.48) 0%, rgba(255,181,52,0.22) 15%, transparent 42%), repeating-conic-gradient(from -8deg at 50% 44%, rgba(255,214,117,0.14) 0deg 2deg, transparent 2deg 11deg)",
                                    animation: "diwaliSkyReveal 2400ms ease-out 80ms both",
                                }}
                            />
                            {[0, 1, 2].map((wave) => (
                                <Box
                                    key={`diwali-shockwave-${wave}`}
                                    aria-hidden="true"
                                    sx={{
                                        position: "absolute",
                                        left: "50%",
                                        top: "44%",
                                        width: `${30 + wave * 7}vmin`,
                                        height: `${30 + wave * 7}vmin`,
                                        borderRadius: "50%",
                                        border: `${wave === 0 ? 3 : 2}px solid ${wave === 1 ? "rgba(255,205,92,0.76)" : "rgba(255,246,207,0.68)"}`,
                                        boxShadow: "0 0 24px rgba(255,177,58,0.2), inset 0 0 18px rgba(255,207,109,0.12)",
                                        animation: `diwaliShockwave ${1450 + wave * 180}ms cubic-bezier(0.16, 1, 0.3, 1) ${120 + wave * 230}ms both`,
                                    }}
                                />
                            ))}
                            <Box
                                aria-hidden="true"
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    bottom: 0,
                                    left: "-22%",
                                    width: "35%",
                                    opacity: 0,
                                    background: "linear-gradient(90deg, transparent, rgba(255,224,151,0.18), rgba(255,255,240,0.34), transparent)",
                                    transform: "skewX(-14deg)",
                                    animation: "diwaliLightSweep 1500ms ease-out 120ms both",
                                }}
                            />
                        </>
                    )}
                    {SPARKLES.map((sparkle, index) => {
                        const sparkleColor =
                            fireworkColors[sparkle.colorIndex % fireworkColors.length];

                        return (
                            <Box
                                key={`sparkle-${index}`}
                                component="span"
                                sx={{
                                    position: "absolute",
                                    left: sparkle.left,
                                    right: sparkle.right,
                                    top: sparkle.top,
                                    fontSize: sparkle.size,
                                    lineHeight: 1,
                                    color: sparkleColor,
                                    textShadow: `0 0 18px ${alpha(sparkleColor, 0.9)}`,
                                    animation: reducedMotion
                                        ? "none"
                                        : `celebrationTwinkle ${sparkle.duration}s ease-in-out ${sparkle.delay}s infinite`,
                                }}
                            >
                                ✦
                            </Box>
                        );
                    })}

                    {isBirthday &&
                        BALLOONS.map((balloon, index) => {
                            const balloonColor =
                                fireworkColors[balloon.colorIndex % fireworkColors.length];

                            return (
                                <Box
                                    key={`balloon-${index}`}
                                    sx={{
                                        position: "absolute",
                                        left: balloon.left,
                                        right: balloon.right,
                                        top: balloon.top,

                                        width: balloon.size,
                                        height: balloon.size * 1.18,

                                        borderRadius: "50% 50% 46% 46%",

                                        background: `
                        radial-gradient(
                          circle at 32% 25%,
                          ${alpha(theme.palette.common.white, 0.75)} 0%,
                          ${alpha(balloonColor, 0.94)} 24%,
                          ${alpha(balloonColor, 0.6)} 100%
                        )
                      `,

                                        boxShadow: `0 0 32px ${alpha(balloonColor, 0.4)}`,

                                        opacity: 0.74,

                                        animation: reducedMotion
                                            ? "none"
                                            : `celebrationBalloon ${balloon.duration}s ease-in-out ${balloon.delay}s infinite alternate`,

                                        "&::before": {
                                            content: '""',
                                            position: "absolute",
                                            left: "50%",
                                            bottom: -8,

                                            width: 0,
                                            height: 0,

                                            borderLeft: "5px solid transparent",
                                            borderRight: "5px solid transparent",
                                            borderTop: `10px solid ${alpha(balloonColor, 0.82)}`,

                                            transform: "translateX(-50%)",
                                        },

                                        "&::after": {
                                            content: '""',
                                            position: "absolute",
                                            left: "50%",
                                            top: "100%",

                                            width: 1,
                                            height: 70,

                                            backgroundColor: alpha(theme.palette.common.white, 0.28),
                                        },
                                    }}
                                />
                            );
                        })}
                </Box>

                <IconButton
                    aria-label="Close celebration"
                    onClick={handleClose}
                    sx={{
                        position: "fixed",
                        top: {
                            xs: 14,
                            sm: 24,
                        },
                        right: {
                            xs: 14,
                            sm: 24,
                        },
                        zIndex: 15,

                        color: "common.white",

                        border: (currentTheme) =>
                            `1px solid ${alpha(currentTheme.palette.common.white, 0.3)}`,

                        bgcolor: (currentTheme) =>
                            alpha(currentTheme.palette.common.black, 0.3),

                        backdropFilter: "blur(10px)",

                        transition: "all 240ms ease",

                        "&:hover": {
                            bgcolor: (currentTheme) =>
                                alpha(currentTheme.palette.common.black, 0.55),

                            transform: "rotate(90deg) scale(1.08)",
                        },
                    }}
                >
                    <FeatherIcon icon="x" size="22" />
                </IconButton>

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 5,

                        width: "100%",
                        maxWidth: 1150,

                        mx: "auto",
                        my: "auto",

                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",

                        textAlign: "center",
                        background: "transparent",

                        "@media (max-height: 740px)": {
                            transform: "scale(0.82)",
                            transformOrigin: "center",
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            gap: 1.5,

                            mb: {
                                xs: 1,
                                md: 1.5,
                            },

                            opacity: 0,

                            animation:
                                "celebrationRise 600ms cubic-bezier(0.16, 1, 0.3, 1) 180ms forwards",

                            "&::before, &::after": {
                                content: '""',

                                width: {
                                    xs: 35,
                                    sm: 75,
                                },

                                height: 2,

                                background: rainbowGradient,

                                opacity: 0.8,
                            },

                            "@media (prefers-reduced-motion: reduce)": {
                                opacity: 1,
                                animation: "none",
                            },
                        }}
                    >
                        <FeatherIcon icon={labelIcon} size="18" />

                        <Typography
                            variant="overline"
                            sx={{
                                color: "common.white",

                                fontSize: {
                                    xs: "0.7rem",
                                    sm: "0.85rem",
                                },

                                fontWeight: 900,

                                letterSpacing: {
                                    xs: 2,
                                    sm: 4,
                                },

                                textShadow: (currentTheme) =>
                                    `0 0 18px ${alpha(currentTheme.palette.common.white, 0.55)}`,
                            }}
                        >
                            {celebrationLabel}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            position: "relative",

                            width: {
                                xs: 125,
                                sm: 155,
                                md: 190,
                            },

                            height: {
                                xs: 125,
                                sm: 155,
                                md: 190,
                            },

                            mb: {
                                xs: 1,
                                md: 1.5,
                            },

                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            opacity: 0,

                            animation: reducedMotion
                                ? "none"
                                : isDiwali
                                    ? "diwaliHeroImpact 1150ms cubic-bezier(0.16, 1, 0.3, 1) 320ms both, celebrationFloat 3s ease-in-out 1.8s infinite"
                                    : "celebrationCakeEntrance 900ms cubic-bezier(0.34, 1.56, 0.64, 1) 300ms forwards",

                            "@media (prefers-reduced-motion: reduce)": {
                                opacity: 1,
                                animation: "none",
                            },
                        }}
                    >
                        <Box
                            aria-hidden="true"
                            sx={{
                                position: "absolute",
                                inset: -10,

                                borderRadius: "50%",

                                background: rainbowGradient,

                                filter: "blur(32px)",

                                animation: reducedMotion
                                    ? "none"
                                    : "celebrationGlow 2.2s ease-in-out infinite",
                            }}
                        />

                        <Box
                            component="span"
                            role="img"
                            aria-label={heroLabel}
                            sx={{
                                position: "relative",
                                zIndex: 2,

                                fontSize: {
                                    xs: "6rem",
                                    sm: "7.5rem",
                                    md: "9rem",
                                },

                                lineHeight: 1,

                                filter: (currentTheme) =>
                                    `drop-shadow(0 22px 28px ${alpha(
                                        currentTheme.palette.common.black,
                                        0.55,
                                    )})`,

                                animation: reducedMotion
                                    ? "none"
                                    : "celebrationFloat 3s ease-in-out infinite",
                            }}
                        >
                            {heroSymbol}
                        </Box>

                        <Box
                            aria-hidden="true"
                            component="span"
                            sx={{
                                position: "absolute",
                                top: "7%",
                                right: "4%",

                                color: "warning.main",

                                fontSize: {
                                    xs: 25,
                                    md: 34,
                                },

                                textShadow: (currentTheme) =>
                                    `0 0 20px ${alpha(currentTheme.palette.warning.main, 0.9)}`,

                                animation: reducedMotion
                                    ? "none"
                                    : "celebrationTwinkle 1.7s ease-in-out infinite",
                            }}
                        >
                            ✦
                        </Box>

                        <Box
                            aria-hidden="true"
                            component="span"
                            sx={{
                                position: "absolute",
                                left: "2%",
                                bottom: "18%",

                                color: "secondary.main",

                                fontSize: {
                                    xs: 20,
                                    md: 28,
                                },

                                textShadow: (currentTheme) =>
                                    `0 0 18px ${alpha(
                                        currentTheme.palette.secondary.main,
                                        0.85,
                                    )}`,

                                animation: reducedMotion
                                    ? "none"
                                    : "celebrationTwinkle 2.1s ease-in-out 400ms infinite",
                            }}
                        >
                            ✦
                        </Box>
                    </Box>

                    <Typography
                        component="h1"
                        sx={{
                            maxWidth: 1080,

                            px: 1,

                            fontSize: {
                                xs: "2.15rem",
                                sm: "3.5rem",
                                md: "5rem",
                                lg: "5.8rem",
                            },

                            lineHeight: 1,

                            fontWeight: 950,

                            letterSpacing: {
                                xs: "-0.035em",
                                md: "-0.055em",
                            },

                            backgroundImage: rainbowGradient,

                            backgroundSize: "400% 400%",

                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",

                            color: "transparent",

                            WebkitTextFillColor: "transparent",

                            filter: (currentTheme) =>
                                `drop-shadow(0 8px 22px ${alpha(
                                    currentTheme.palette.common.black,
                                    0.8,
                                )})`,

                            opacity: 0,

                            animation: reducedMotion
                                ? "celebrationRise 350ms ease 450ms forwards"
                                : isDiwali
                                    ? "diwaliTitleSlam 1050ms cubic-bezier(0.16, 1, 0.3, 1) 700ms both, celebrationRainbow 4.4s ease infinite 1.9s"
                                    : "celebrationRise 700ms cubic-bezier(0.16, 1, 0.3, 1) 500ms forwards, celebrationRainbow 5s ease infinite 1.1s",

                            "@media (prefers-reduced-motion: reduce)": {
                                animation: "celebrationRise 350ms ease 450ms forwards",
                            },
                        }}
                    >
                        {mainHeading}
                    </Typography>

                    {isDiwali && (
                        <Typography
                            component="div"
                            sx={{
                                mt: { xs: 0.8, sm: 1.2 },
                                px: 1,
                                color: "#ffe6a6",
                                fontSize: { xs: "0.66rem", sm: "0.88rem" },
                                fontWeight: 900,
                                letterSpacing: { xs: 2, sm: 4 },
                                textTransform: "uppercase",
                                textShadow: "0 0 18px rgba(255, 173, 47, 0.78)",
                                opacity: 0,
                                animation: "celebrationRise 650ms ease 620ms forwards",
                            }}
                        >
                            Light up the night · Sparkle together
                        </Typography>
                    )}

                    {showSeparateName && (
                        <Typography
                            component="div"
                            sx={{
                                maxWidth: 1050,

                                mt: {
                                    xs: 0.5,
                                    md: 0.8,
                                },

                                px: 1,

                                fontSize: {
                                    xs: "2.4rem",
                                    sm: "3.9rem",
                                    md: "5.5rem",
                                    lg: "6.3rem",
                                },

                                lineHeight: 1,

                                fontWeight: 950,

                                letterSpacing: "-0.05em",

                                overflowWrap: "anywhere",

                                backgroundImage: rainbowGradient,

                                backgroundSize: "400% 400%",

                                backgroundClip: "text",

                                WebkitBackgroundClip: "text",

                                color: "transparent",

                                WebkitTextFillColor: "transparent",

                                filter: (currentTheme) =>
                                    `drop-shadow(0 9px 26px ${alpha(
                                        currentTheme.palette.common.black,
                                        0.82,
                                    )})`,

                                opacity: 0,

                                animation: reducedMotion
                                    ? "celebrationRise 350ms ease 580ms forwards"
                                    : "celebrationRise 750ms cubic-bezier(0.16, 1, 0.3, 1) 600ms forwards, celebrationRainbow 4.5s ease infinite 1.2s",
                            }}
                        >
                            {personName}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            maxWidth: 780,

                            mt: {
                                xs: 1.8,
                                sm: 2.2,
                            },

                            px: {
                                xs: 1,
                                sm: 2,
                            },

                            fontSize: {
                                xs: "1rem",
                                sm: "1.2rem",
                                md: "1.35rem",
                            },

                            lineHeight: 1.65,
                            fontWeight: 550,

                            color: (currentTheme) =>
                                alpha(currentTheme.palette.common.white, 0.94),

                            textShadow: (currentTheme) =>
                                `0 4px 18px ${alpha(currentTheme.palette.common.black, 0.9)}`,

                            opacity: 0,

                            animation: "celebrationRise 650ms ease 760ms forwards",
                        }}
                    >
                        {computedMessage}
                    </Typography>

                    {celebration.sentBy && (
                        <Box
                            sx={{
                                mt: {
                                    xs: 2,
                                    sm: 2.5,
                                },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: {
                                    xs: 1.2,
                                    sm: 2,
                                },
                                width: "100%",
                                opacity: 0,
                                animation: "celebrationRise 600ms ease 900ms forwards",
                                "&::before, &::after": {
                                    content: '""',
                                    width: {
                                        xs: 28,
                                        sm: 70,
                                    },
                                    height: 1,
                                    background: (currentTheme) =>
                                        `linear-gradient(
                                            90deg,
                                            transparent,
                                            ${alpha(
                                            currentTheme.palette.common.white,
                                            0.65,
                                        )}
                                        )`,
                                },
                                "&::after": {
                                    transform: "rotate(180deg)",
                                },
                            }}
                        >
                            <Typography
                                component="div"
                                sx={{
                                    fontSize: {
                                        xs: "1rem",
                                        sm: "1.2rem",
                                        md: "1.35rem",
                                    },
                                    fontWeight: 600,
                                    letterSpacing: {
                                        xs: 0.4,
                                        sm: 0.8,
                                    },
                                    color: (currentTheme) =>
                                        alpha(currentTheme.palette.common.white, 0.9),
                                    textShadow: (currentTheme) =>
                                        `0 4px 16px ${alpha(
                                            currentTheme.palette.common.black,
                                            0.9,
                                        )}`,
                                    whiteSpace: {
                                        xs: "normal",
                                        sm: "nowrap",
                                    },
                                }}
                            >
                                Celebrated by{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        fontWeight: 900,
                                        backgroundImage: rainbowGradient,
                                        backgroundSize: "300% 300%",
                                        backgroundClip: "text",
                                        WebkitBackgroundClip: "text",
                                        color: "transparent",
                                        WebkitTextFillColor: "transparent",
                                        animation: reducedMotion
                                            ? "none"
                                            : "celebrationRainbow 4s ease infinite",
                                    }}
                                >
                                    {celebration.sentBy}
                                </Box>
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box
                    aria-hidden="true"
                    sx={{
                        position: "fixed",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 20,

                        height: 5,

                        transformOrigin: "left center",

                        backgroundImage: rainbowGradient,

                        backgroundSize: "300% 100%",

                        animation: `
              celebrationProgress ${durationMs}ms linear forwards,
              celebrationRainbow 3s linear infinite
            `,
                    }}
                />
            </Box>
        </Fade>
    );
};

const DashboardSignalRContext = createContext(null);

export const useDashboardSignalR = () => {
    const context = useContext(DashboardSignalRContext);

    if (!context) {
        throw new Error(
            "useDashboardSignalR must be used inside DashboardSignalRProvider.",
        );
    }

    return context;
};

const getCurrentUser = async () => {
    const response = await GetUserDetail();

    let userData =
        response?.data?.result ?? response?.data?.data ?? response?.data ?? {};

    if (Array.isArray(userData)) {
        userData = userData[0] ?? {};
    }

    const personId =
        userData.personId ??
        userData.PersonId ??
        userData.personID ??
        userData.employeeId ??
        userData.EmployeeId ??
        userData.id ??
        userData.Id;

    if (
        personId === undefined ||
        personId === null ||
        String(personId).trim() === ""
    ) {
        throw new Error("Person ID was not returned by GetUserDetail.");
    }

    const firstName = userData.firstName ?? userData.FirstName ?? "";

    const lastName = userData.lastName ?? userData.LastName ?? "";

    const suppliedName =
        userData.personName ??
        userData.PersonName ??
        userData.fullName ??
        userData.FullName;

    const normalizedPersonId = String(personId).trim();

    const personName = String(
        suppliedName ||
        `${firstName} ${lastName}`.trim() ||
        `User ${normalizedPersonId}`,
    ).trim();

    return {
        personId: normalizedPersonId,
        personName: personName || `User ${normalizedPersonId}`,
    };
};

export const DashboardSignalRProvider = ({ children }) => {
    const connectionRef = useRef(null);
    const registeredUserRef = useRef(null);
    const mountedRef = useRef(false);
    const startPromiseRef = useRef(null);
    const celebrationQueueRef = useRef([]);
    const activeCelebrationRef = useRef(null);
    const receivedCelebrationIdsRef = useRef(new Set());

    const celebratedUsersByKeyRef = useRef(new Map());

    const [onlineDashboardUsers, setOnlineDashboardUsers] = useState([]);

    const [completedCelebrationKeys, setCompletedCelebrationKeys] = useState([]);

    /*
     * Stores the cumulative colleague list by celebration key.
     * This is exposed to Dashboard1 and is not rendered in the popup.
     */
    const [celebratedUsersByKey, setCelebratedUsersByKey] = useState({});

    const [dashboardSignalRStatus, setDashboardSignalRStatus] = useState(
        CONNECTION_STATUS.CONNECTING,
    );

    const [dashboardSignalRError, setDashboardSignalRError] = useState("");

    const [activeCelebration, setActiveCelebration] = useState(null);

    const updateOnlineUsers = useCallback((users) => {
        if (!mountedRef.current) {
            return;
        }

        setOnlineDashboardUsers(normalizeOnlineUsers(users));
    }, []);

    const replaceCompletedCelebrationKeys = useCallback((keys) => {
        if (!mountedRef.current) {
            return;
        }

        setCompletedCelebrationKeys(normalizeCelebrationKeys(keys));
    }, []);

    const addCompletedCelebrationKey = useCallback((celebrationKey) => {
        const normalizedKey = String(celebrationKey ?? "").trim();

        if (!normalizedKey) {
            return;
        }

        setCompletedCelebrationKeys((currentKeys) => {
            if (currentKeys.includes(normalizedKey)) {
                return currentKeys;
            }

            return [...currentKeys, normalizedKey];
        });
    }, []);

    const prepareCelebrationForCurrentUser = useCallback((celebration) => {
        if (!celebration) {
            return celebration;
        }

        const celebrationKey = String(
            celebration?.celebrationKey ??
            celebration?.CelebrationKey ??
            "",
        ).trim();

        const currentPersonId = String(
            registeredUserRef.current?.personId ?? "",
        ).trim();

        const targetPersonId = String(
            celebration?.personId ??
            celebration?.PersonId ??
            celebration?.targetPersonId ??
            celebration?.TargetPersonId ??
            "",
        ).trim();

        const currentPersonName = String(
            registeredUserRef.current?.personName ?? "",
        )
            .trim()
            .replace(/\s+/g, " ")
            .toLowerCase();

        const targetPersonName = String(
            celebration?.personName ??
            celebration?.PersonName ??
            celebration?.targetPersonName ??
            celebration?.TargetPersonName ??
            "",
        )
            .trim()
            .replace(/\s+/g, " ")
            .toLowerCase();

        const isTargetCelebration = Boolean(
            (currentPersonId &&
                targetPersonId &&
                currentPersonId === targetPersonId) ||
            (currentPersonName &&
                targetPersonName &&
                currentPersonName === targetPersonName),
        );

        const existingUsers = celebrationKey
            ? celebratedUsersByKeyRef.current.get(celebrationKey) || []
            : [];

        const mergedUsers = mergeCelebratedUsers(
            existingUsers,
            celebration?.celebratedUsers ??
            celebration?.CelebratedUsers,
        );

        if (celebrationKey && mergedUsers.length > 0) {
            celebratedUsersByKeyRef.current.set(celebrationKey, mergedUsers);

            if (isTargetCelebration) {
                setCelebratedUsersByKey((currentLists) => ({
                    ...currentLists,
                    [celebrationKey]: mergedUsers,
                }));
            }
        }

        return {
            ...celebration,
            isTargetCelebration,
            celebratedUsers: isTargetCelebration ? mergedUsers : [],
        };
    }, []);

    const updateCelebratedUsersForTarget = useCallback((celebratedUsersState) => {
        const celebrationKey = String(
            celebratedUsersState?.celebrationKey ?? "",
        ).trim();

        const celebrationNameKey = String(
            celebratedUsersState?.celebrationNameKey ?? "",
        ).trim();

        if (!celebrationKey && !celebrationNameKey) {
            return;
        }

        const currentUsers =
            (celebrationKey
                ? celebratedUsersByKeyRef.current.get(celebrationKey)
                : null) ||
            (celebrationNameKey
                ? celebratedUsersByKeyRef.current.get(celebrationNameKey)
                : null) ||
            [];

        const mergedUsers = mergeCelebratedUsers(
            currentUsers,
            celebratedUsersState?.celebratedUsers,
        );

        if (celebrationKey) {
            celebratedUsersByKeyRef.current.set(celebrationKey, mergedUsers);
        }

        if (celebrationNameKey) {
            celebratedUsersByKeyRef.current.set(celebrationNameKey, mergedUsers);
        }

        const currentPersonId = String(
            registeredUserRef.current?.personId ?? "",
        ).trim();

        const targetPersonId = String(
            celebratedUsersState?.personId ??
            celebratedUsersState?.targetPersonId ??
            "",
        ).trim();

        const currentPersonName = String(
            registeredUserRef.current?.personName ?? "",
        )
            .trim()
            .replace(/\s+/g, " ")
            .toLowerCase();

        const targetPersonName = String(
            celebratedUsersState?.personName ??
            celebratedUsersState?.targetPersonName ??
            "",
        )
            .trim()
            .replace(/\s+/g, " ")
            .toLowerCase();

        const isCurrentTarget = Boolean(
            (currentPersonId &&
                targetPersonId &&
                currentPersonId === targetPersonId) ||
            (currentPersonName &&
                targetPersonName &&
                currentPersonName === targetPersonName),
        );

        if (!isCurrentTarget) {
            return;
        }

        setCelebratedUsersByKey((currentLists) => ({
            ...currentLists,
            ...(celebrationKey
                ? {
                    [celebrationKey]: mergedUsers,
                }
                : {}),
            ...(celebrationNameKey
                ? {
                    [celebrationNameKey]: mergedUsers,
                }
                : {}),
        }));

        const activeCelebration = activeCelebrationRef.current;

        if (
            activeCelebration &&
            String(activeCelebration?.celebrationKey ?? "").trim() === celebrationKey
        ) {
            const updatedCelebration = {
                ...activeCelebration,
                isTargetCelebration: true,
                celebratedUsers: mergedUsers,
            };

            activeCelebrationRef.current = updatedCelebration;

            setActiveCelebration(updatedCelebration);
        }

        celebrationQueueRef.current = celebrationQueueRef.current.map(
            (queuedCelebration) =>
                String(queuedCelebration?.celebrationKey ?? "").trim() ===
                    celebrationKey
                    ? {
                        ...queuedCelebration,
                        isTargetCelebration: true,
                        celebratedUsers: mergedUsers,
                    }
                    : queuedCelebration,
        );
    }, []);

    const showBackgroundNotification = useCallback((celebration) => {
        if (
            typeof window === "undefined" ||
            !("Notification" in window) ||
            Notification.permission !== "granted"
        ) {
            return;
        }

        try {
            const notification = new Notification(
                celebration?.title || "A celebration is waiting!",
                {
                    body:
                        celebration?.message ||
                        "Return to the application to view the celebration.",
                    tag: getCelebrationIdentity(celebration),
                    requireInteraction: true,
                },
            );

            notification.onclick = () => {
                window.focus();
                notification.close();
            };
        } catch (error) {
            console.error("Unable to show celebration notification:", error);
        }
    }, []);

    const acknowledgeDisplayedCelebration = useCallback(
        async (celebration) => {
            const celebrationRecordId = Number(
                celebration?.celebrationRecordId ??
                celebration?.CelebrationRecordId ??
                0,
            );

            const connection = connectionRef.current;

            if (
                celebrationRecordId <= 0 ||
                !isConnected(connection)
            ) {
                return;
            }

            try {
                await connection.invoke(
                    HUB_METHODS.ACKNOWLEDGE_CELEBRATION,
                    celebrationRecordId,
                );
            } catch (error) {
                /*
                 * Do not remove the animation from the in-memory queue on
                 * acknowledgement failure. The SQL view row can be written
                 * on the close retry or on a later attempt.
                 */
                console.error(
                    "Unable to acknowledge displayed celebration:",
                    error,
                );
            }
        },
        [],
    );

    const showNextCelebration = useCallback(() => {
        if (
            !mountedRef.current ||
            activeCelebrationRef.current
        ) {
            return;
        }

        const nextCelebration = celebrationQueueRef.current.shift();

        if (!nextCelebration) {
            return;
        }

        activeCelebrationRef.current = nextCelebration;
        setActiveCelebration(nextCelebration);

        /*
         * Mark this celebration as viewed for THIS logged-in user as soon
         * as the animation starts. This prevents the same animation from
         * replaying after a browser refresh. The backend stores this in
         * DashboardCelebrationView, so other users still have their own
         * independent pending list.
         */
        void acknowledgeDisplayedCelebration(nextCelebration);
    }, [acknowledgeDisplayedCelebration]);

    const enqueueCelebration = useCallback(
        (celebration) => {
            if (!celebration) {
                return;
            }

            const preparedCelebration = prepareCelebrationForCurrentUser(celebration);

            const celebrationIdentity = getCelebrationIdentity(preparedCelebration);

            if (receivedCelebrationIdsRef.current.has(celebrationIdentity)) {
                return;
            }

            receivedCelebrationIdsRef.current.add(celebrationIdentity);

            if (receivedCelebrationIdsRef.current.size > 500) {
                const oldestIdentities = Array.from(
                    receivedCelebrationIdsRef.current,
                ).slice(0, 200);

                oldestIdentities.forEach((identity) => {
                    receivedCelebrationIdsRef.current.delete(identity);
                });
            }

            celebrationQueueRef.current.push(preparedCelebration);

            if (!isApplicationActive()) {
                showBackgroundNotification(preparedCelebration);

                return;
            }

            showNextCelebration();
        },
        [
            prepareCelebrationForCurrentUser,
            showBackgroundNotification,
            showNextCelebration,
        ],
    );

    const triggerDiwaliCelebration = useCallback(() => {
        const localId = `local-diwali-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        enqueueCelebration({
            celebrationId: localId,
            celebrationType: "diwali",
            title: "Happy Diwali",
            message:
                "May the lights of Diwali fill every heart with joy, every home with prosperity, and every new beginning with possibility. Wishing our entire team a bright and beautiful festival!",
            autoCloseMilliseconds: 18000,
            sentAt: new Date().toISOString(),
        });
    }, [enqueueCelebration]);

    const finishActiveCelebration = useCallback(async () => {
        const celebration = activeCelebrationRef.current;

        if (!celebration) {
            return;
        }

        activeCelebrationRef.current = null;
        setActiveCelebration(null);

        // Safe retry. Backend acknowledgement is idempotent per viewer.
        await acknowledgeDisplayedCelebration(celebration);
    }, [acknowledgeDisplayedCelebration]);

    const dismissCelebration = useCallback(async () => {
        const celebration = activeCelebrationRef.current;

        activeCelebrationRef.current = null;
        setActiveCelebration(null);

        // Safe retry. Backend acknowledgement is idempotent per viewer.
        await acknowledgeDisplayedCelebration(celebration);

        window.setTimeout(showNextCelebration, 100);
    }, [acknowledgeDisplayedCelebration, showNextCelebration]);

    const replaceReceivedCelebrations = useCallback((summaries) => {
        if (!mountedRef.current) {
            return;
        }

        const nextMap = new Map();

        const nextLists = {};

        (Array.isArray(summaries) ? summaries : []).forEach((summary) => {
            const celebrationKey = String(
                summary?.celebrationKey ?? summary?.CelebrationKey ?? "",
            ).trim();

            if (!celebrationKey) {
                return;
            }

            const celebrationNameKey = String(
                summary?.celebrationNameKey ?? summary?.CelebrationNameKey ?? "",
            ).trim();

            const users = normalizeCelebratedUsers(
                summary?.celebratedUsers ?? summary?.CelebratedUsers,
            );

            nextMap.set(celebrationKey, users);

            nextLists[celebrationKey] = users;

            if (celebrationNameKey) {
                nextMap.set(celebrationNameKey, users);

                nextLists[celebrationNameKey] = users;
            }
        });

        celebratedUsersByKeyRef.current = nextMap;

        setCelebratedUsersByKey(nextLists);
    }, []);

    const refreshUsersFromConnection = useCallback(
        async (connection) => {
            if (!isConnected(connection)) {
                return [];
            }

            const users = await connection.invoke(HUB_METHODS.GET_ONLINE_USERS);

            updateOnlineUsers(users);

            return normalizeOnlineUsers(users);
        },
        [updateOnlineUsers],
    );

    const refreshCompletedKeysFromConnection = useCallback(
        async (connection) => {
            if (!isConnected(connection)) {
                return [];
            }

            const keys = await connection.invoke(
                HUB_METHODS.GET_MY_COMPLETED_CELEBRATION_KEYS,
            );

            replaceCompletedCelebrationKeys(keys);

            return normalizeCelebrationKeys(keys);
        },
        [replaceCompletedCelebrationKeys],
    );

    const refreshReceivedCelebrationsFromConnection = useCallback(
        async (connection) => {
            if (!isConnected(connection)) {
                return [];
            }

            const summaries = await connection.invoke(
                HUB_METHODS.GET_MY_RECEIVED_CELEBRATIONS,
            );

            replaceReceivedCelebrations(summaries);

            return Array.isArray(summaries) ? summaries : [];
        },
        [replaceReceivedCelebrations],
    );

    /*
     * Do not auto-load pending celebration animations during login/reconnect.
     * Dashboard1 starts them only when the user clicks "View Celebrations".
     */
    const registerCurrentUser = useCallback(
        async (connection) => {
            const currentUser = registeredUserRef.current;

            if (!currentUser || !isConnected(connection)) {
                return;
            }

            await connection.invoke(
                HUB_METHODS.REGISTER_USER,
                currentUser.personId,
                currentUser.personName,
                isApplicationActive(),
            );

            /*
             * The live SignalR registration and online-user refresh
             * are required. Database-backed celebration history is
             * optional during connection startup and must not mark
             * the working SignalR transport as Failed.
             */
            await refreshUsersFromConnection(connection);

            try {
                await refreshCompletedKeysFromConnection(connection);
            } catch (error) {
                console.error("Unable to load completed celebration keys:", error);

                if (mountedRef.current) {
                    setDashboardSignalRError(
                        error?.message ||
                        "SignalR is connected, but completed celebration history could not be loaded.",
                    );
                }
            }

            try {
                await refreshReceivedCelebrationsFromConnection(connection);
            } catch (error) {
                console.error("Unable to load received celebration summaries:", error);

                if (mountedRef.current) {
                    setDashboardSignalRError(
                        error?.message ||
                        "SignalR is connected, but received celebration history could not be loaded.",
                    );
                }
            }

        },
        [
            refreshCompletedKeysFromConnection,
            refreshReceivedCelebrationsFromConnection,
            refreshUsersFromConnection,
        ],
    );

    useEffect(() => {
        mountedRef.current = true;

        let disposed = false;
        let connection = null;
        let restartTimer = null;

        const hubBaseUrl = resolveDashboardHubBaseUrl();

        if (!hubBaseUrl) {
            setDashboardSignalRStatus(CONNECTION_STATUS.CONFIGURATION_MISSING);

            setDashboardSignalRError(
                "REACT_APP_SIGNALR_API_URL or REACT_APP_URL is not configured.",
            );

            return () => {
                mountedRef.current = false;
            };
        }

        const safelySetStatus = (status) => {
            if (!disposed && mountedRef.current) {
                setDashboardSignalRStatus(status);
            }
        };

        const safelySetError = (message) => {
            if (!disposed && mountedRef.current) {
                setDashboardSignalRError(message || "");
            }
        };

        const handleOnlineUsersChanged = (users) => {
            updateOnlineUsers(users);
        };

        const handleCelebration = (celebration) => {
            if (disposed || !mountedRef.current) {
                return;
            }

            enqueueCelebration(celebration);
        };

        const handleCelebrationStateChanged = (celebrationState) => {
            const isCelebrated =
                celebrationState?.isCelebrated ??
                celebrationState?.IsCelebrated ??
                true;

            if (isCelebrated === false) {
                return;
            }

            addCompletedCelebrationKey(
                celebrationState?.celebrationKey ??
                celebrationState?.CelebrationKey,
            );

            addCompletedCelebrationKey(
                celebrationState?.celebrationNameKey ??
                celebrationState?.CelebrationNameKey,
            );
        };

        const handleCelebratedUsersChanged = (celebratedUsersState) => {
            updateCelebratedUsersForTarget(celebratedUsersState);
        };

        const updatePresence = async () => {
            if (!isConnected(connection)) {
                return;
            }

            await connection.invoke(
                HUB_METHODS.UPDATE_PRESENCE,
                isApplicationActive(),
            );
        };

        const startConnection = async ({ showError = true } = {}) => {
            if (disposed || !connection) {
                return false;
            }

            if (isConnected(connection)) {
                await registerCurrentUser(connection);

                return true;
            }

            if (connection.state !== signalR.HubConnectionState.Disconnected) {
                return false;
            }

            if (startPromiseRef.current) {
                return startPromiseRef.current;
            }

            const startPromise = (async () => {
                safelySetStatus(CONNECTION_STATUS.CONNECTING);

                try {
                    await connection.start();

                    if (disposed) {
                        await connection.stop();
                        return false;
                    }

                    safelySetStatus(CONNECTION_STATUS.CONNECTED);

                    safelySetError("");

                    await registerCurrentUser(connection);

                    return true;
                } catch (error) {
                    if (showError && !disposed) {
                        safelySetError(
                            error?.message || "Unable to connect to Dashboard SignalR.",
                        );
                    }

                    return false;
                } finally {
                    startPromiseRef.current = null;
                }
            })();

            startPromiseRef.current = startPromise;

            return startPromise;
        };

        const startWithRetry = async (retryDelays) => {
            for (const delay of retryDelays) {
                if (disposed) {
                    return false;
                }

                if (delay > 0) {
                    await wait(delay);
                }

                if (disposed) {
                    return false;
                }

                const started = await startConnection({
                    showError: false,
                });

                if (started) {
                    return true;
                }
            }

            if (!disposed) {
                safelySetStatus(CONNECTION_STATUS.FAILED);

                safelySetError(
                    "Unable to connect to Dashboard SignalR. Please verify the API and network connection.",
                );
            }

            return false;
        };

        const scheduleManualRestart = () => {
            if (disposed || restartTimer) {
                return;
            }

            restartTimer = window.setTimeout(async () => {
                restartTimer = null;

                const started = await startWithRetry(MANUAL_RESTART_DELAYS);

                if (!started) {
                    scheduleManualRestart();
                }
            }, 5000);
        };

        const handleApplicationActivityChange = async () => {
            if (disposed) {
                return;
            }

            if (isApplicationActive()) {
                /*
                 * Only celebrations that have never started
                 * are eligible to display when the user returns.
                 */
                showNextCelebration();
            } else {
                /*
                 * Do not requeue an animation that already began.
                 * This prevents replay on every tab/focus change.
                 */
                await finishActiveCelebration();
            }

            if (isConnected(connection)) {
                try {
                    await updatePresence();
                    await refreshUsersFromConnection(connection);
                } catch (error) {
                    console.error("Unable to update Dashboard presence:", error);
                }

                return;
            }

            await startWithRetry([0, 2000, 5000]);
        };

        const initializeConnection = async () => {
            try {
                registeredUserRef.current = await getCurrentUser();

                if (disposed) {
                    return;
                }

                connection = new signalR.HubConnectionBuilder()
                    .withUrl(`${hubBaseUrl}${HUB_ROUTE}`, {
                        withCredentials: true,
                    })
                    .withAutomaticReconnect(AUTOMATIC_RECONNECT_DELAYS)
                    .configureLogging(signalR.LogLevel.Information)
                    .build();

                connection.on(
                    HUB_EVENTS.ONLINE_USERS_CHANGED,
                    handleOnlineUsersChanged,
                );

                connection.on(HUB_EVENTS.SHOW_CELEBRATION, handleCelebration);

                connection.on(
                    HUB_EVENTS.CELEBRATION_STATE_CHANGED,
                    handleCelebrationStateChanged,
                );

                connection.on(
                    HUB_EVENTS.CELEBRATED_USERS_CHANGED,
                    handleCelebratedUsersChanged,
                );

                connection.onreconnecting(() => {
                    safelySetStatus(CONNECTION_STATUS.RECONNECTING);
                });

                connection.onreconnected(async () => {
                    if (disposed) {
                        return;
                    }

                    safelySetStatus(CONNECTION_STATUS.CONNECTED);

                    safelySetError("");

                    try {
                        await registerCurrentUser(connection);
                    } catch (error) {
                        console.error("Dashboard SignalR re-registration failed:", error);

                        safelySetError(
                            error?.message || "Unable to register after reconnecting.",
                        );
                    }
                });

                connection.onclose((error) => {
                    if (disposed) {
                        return;
                    }

                    safelySetStatus(CONNECTION_STATUS.DISCONNECTED);

                    updateOnlineUsers([]);

                    if (error) {
                        safelySetError(
                            error.message || "Dashboard SignalR connection closed.",
                        );
                    }

                    scheduleManualRestart();
                });

                connectionRef.current = connection;

                document.addEventListener(
                    "visibilitychange",
                    handleApplicationActivityChange,
                );

                window.addEventListener("focus", handleApplicationActivityChange);

                window.addEventListener("blur", handleApplicationActivityChange);

                const started = await startWithRetry(INITIAL_START_RETRY_DELAYS);

                if (!started) {
                    scheduleManualRestart();
                }
            } catch (error) {
                console.error("Dashboard SignalR initialization failed:", error);

                if (!disposed) {
                    safelySetStatus(CONNECTION_STATUS.FAILED);

                    safelySetError(
                        error?.message || "Unable to initialize Dashboard SignalR.",
                    );
                }
            }
        };

        initializeConnection();

        return () => {
            disposed = true;
            mountedRef.current = false;

            if (restartTimer) {
                window.clearTimeout(restartTimer);

                restartTimer = null;
            }

            document.removeEventListener(
                "visibilitychange",
                handleApplicationActivityChange,
            );

            window.removeEventListener("focus", handleApplicationActivityChange);

            window.removeEventListener("blur", handleApplicationActivityChange);

            if (connection) {
                connection.off(
                    HUB_EVENTS.ONLINE_USERS_CHANGED,
                    handleOnlineUsersChanged,
                );

                connection.off(HUB_EVENTS.SHOW_CELEBRATION, handleCelebration);

                connection.off(
                    HUB_EVENTS.CELEBRATION_STATE_CHANGED,
                    handleCelebrationStateChanged,
                );

                connection.off(
                    HUB_EVENTS.CELEBRATED_USERS_CHANGED,
                    handleCelebratedUsersChanged,
                );

                if (connection.state !== signalR.HubConnectionState.Disconnected) {
                    connection.stop().catch((error) => {
                        console.error("Unable to stop Dashboard SignalR cleanly:", error);
                    });
                }
            }

            celebrationQueueRef.current = [];
            activeCelebrationRef.current = null;
            receivedCelebrationIdsRef.current.clear();
            celebratedUsersByKeyRef.current.clear();

            startPromiseRef.current = null;
            connectionRef.current = null;
            registeredUserRef.current = null;
        };
    }, [
        addCompletedCelebrationKey,
        enqueueCelebration,
        finishActiveCelebration,
        refreshCompletedKeysFromConnection,
        refreshUsersFromConnection,
        registerCurrentUser,
        showNextCelebration,
        updateCelebratedUsersForTarget,
        updateOnlineUsers,
    ]);

    const invokeDashboardHub = useCallback(
        async (methodName, ...methodArguments) => {
            const connection = connectionRef.current;

            if (!isConnected(connection)) {
                throw new Error("Dashboard SignalR is not connected.");
            }

            return connection.invoke(methodName, ...methodArguments);
        },
        [],
    );

    const refreshOnlineDashboardUsers = useCallback(async () => {
        const users = await invokeDashboardHub(HUB_METHODS.GET_ONLINE_USERS);

        updateOnlineUsers(users);

        return normalizeOnlineUsers(users);
    }, [invokeDashboardHub, updateOnlineUsers]);

    const refreshMyCompletedCelebrationKeys = useCallback(async () => {
        const keys = await invokeDashboardHub(
            HUB_METHODS.GET_MY_COMPLETED_CELEBRATION_KEYS,
        );

        replaceCompletedCelebrationKeys(keys);

        return normalizeCelebrationKeys(keys);
    }, [invokeDashboardHub, replaceCompletedCelebrationKeys]);

    const refreshMyReceivedCelebrations = useCallback(async () => {
        const summaries = await invokeDashboardHub(
            HUB_METHODS.GET_MY_RECEIVED_CELEBRATIONS,
        );

        replaceReceivedCelebrations(summaries);

        return Array.isArray(summaries) ? summaries : [];
    }, [invokeDashboardHub, replaceReceivedCelebrations]);

    const refreshMyPendingCelebrations = useCallback(async () => {
        const pendingCelebrations = await invokeDashboardHub(
            HUB_METHODS.GET_MY_PENDING_CELEBRATIONS,
        );

        const normalizedPendingCelebrations = Array.isArray(
            pendingCelebrations,
        )
            ? pendingCelebrations
            : [];

        normalizedPendingCelebrations.forEach((celebration) => {
            enqueueCelebration(celebration);
        });

        return normalizedPendingCelebrations;
    }, [enqueueCelebration, invokeDashboardHub]);

    const sendTestCelebration = useCallback(
        async (targetPersonId) => {
            const normalizedTargetId = String(targetPersonId ?? "").trim();

            if (!normalizedTargetId) {
                throw new Error("Please select an online user.");
            }

            return invokeDashboardHub(
                HUB_METHODS.SEND_TEST_CELEBRATION,
                normalizedTargetId,
            );
        },
        [invokeDashboardHub],
    );

    const broadcastCelebration = useCallback(
        async (celebrationRequest) => {
            if (!celebrationRequest || typeof celebrationRequest !== "object") {
                throw new Error("Celebration details are required.");
            }

            const currentPersonId = String(
                registeredUserRef.current?.personId ?? "",
            ).trim();

            const targetPersonId = String(
                celebrationRequest?.personId ?? celebrationRequest?.PersonId ?? "",
            ).trim();

            if (
                currentPersonId &&
                targetPersonId &&
                currentPersonId === targetPersonId
            ) {
                throw new Error("You cannot celebrate yourself.");
            }

            const result = await invokeDashboardHub(
                HUB_METHODS.BROADCAST_CELEBRATION,
                celebrationRequest,
            );

            addCompletedCelebrationKey(
                result?.celebrationKey ??
                result?.CelebrationKey,
            );

            addCompletedCelebrationKey(
                result?.celebrationNameKey ??
                result?.CelebrationNameKey,
            );

            return result;
        },
        [addCompletedCelebrationKey, invokeDashboardHub],
    );

    const enableCelebrationNotifications = useCallback(async () => {
        if (typeof window === "undefined" || !("Notification" in window)) {
            return "unsupported";
        }

        return Notification.requestPermission();
    }, []);

    const isDashboardSignalRConnected =
        dashboardSignalRStatus === CONNECTION_STATUS.CONNECTED;

    const contextValue = useMemo(
        () => ({
            connection: connectionRef.current,

            currentDashboardUser: registeredUserRef.current,

            onlineDashboardUsers,

            completedCelebrationKeys,

            celebratedUsersByKey,

            dashboardSignalRStatus,

            isDashboardSignalRConnected,

            dashboardSignalRError,

            setDashboardSignalRError,

            invokeDashboardHub,

            refreshOnlineDashboardUsers,

            refreshMyCompletedCelebrationKeys,

            refreshMyReceivedCelebrations,

            refreshMyPendingCelebrations,

            sendTestCelebration,

            broadcastCelebration,

            triggerDiwaliCelebration,

            enableCelebrationNotifications,
        }),
        [
            onlineDashboardUsers,
            completedCelebrationKeys,
            celebratedUsersByKey,
            dashboardSignalRStatus,
            isDashboardSignalRConnected,
            dashboardSignalRError,
            invokeDashboardHub,
            refreshOnlineDashboardUsers,
            refreshMyCompletedCelebrationKeys,
            refreshMyReceivedCelebrations,
            refreshMyPendingCelebrations,
            sendTestCelebration,
            broadcastCelebration,
            triggerDiwaliCelebration,
            enableCelebrationNotifications,
        ],
    );

    return (
        <DashboardSignalRContext.Provider value={contextValue}>
            {children}

            <CelebrationOverlay
                celebration={activeCelebration}
                onClose={dismissCelebration}
            />
        </DashboardSignalRContext.Provider>
    );
};

DashboardSignalRProvider.displayName = "DashboardSignalRProvider";
